import React, { useEffect, useRef, useState } from "react";
import type { GameState } from "../../shared/types";

import Overlay from "./Overlay";
import requestFrame from "../../shared/request-frame.js";
import * as gsf from "core/src/core/game-state-functions.js";

// The numbers always count from here, whatever the countdown's length.
const COUNT_FROM = 3;

interface Props {
  gameState: GameState;
  getRenderTime: () => number;
}

export default function Countdown({ gameState, getRenderTime }: Props) {
  // Re-render every animation frame so the countdown number/animation tracks render
  // time. A rAF loop (not a bare setInterval) syncs the re-render to the display's
  // actual paint rate: a 10ms interval fires ~100/s regardless of refresh rate, which
  // showed up as the single biggest CPU cost in a production profile during a round
  // start for no visible benefit over matching the screen.
  // Only while the numbers are on screen, though (plus one frame to clear them):
  // the loop keeps a cheap per-frame check for the next round's countdown, but it
  // used to re-render this component on every frame of the whole round.
  const [, setTick] = useState(0);
  const propsRef = useRef({ gameState, getRenderTime });
  propsRef.current = { gameState, getRenderTime };
  useEffect(() => {
    let id: number;
    let shown = true;
    const loop = () => {
      const { gameState: gs, getRenderTime: now } = propsRef.current;
      const left = gs.startPhaseTime - now();
      const visible = left > -1 && left <= gsf.getCountdownLength(gs);
      if (visible || shown) setTick((t) => t + 1);
      shown = visible;
      id = requestFrame(loop);
    };
    id = requestFrame(loop);
    return () => window.cancelAnimationFrame(id);
  }, []);

  const timeLeft = gameState.startPhaseTime - getRenderTime();
  if (timeLeft <= -1) {
    return null;
  }
  // The start phase opens with the roll call (round-intro.ts); the numbers
  // only run through the countdown proper that follows it, and never from
  // higher than 3: a longer countdown (a ranked match's first, sized to the
  // match-found banner) is silence until its last three seconds.
  if (timeLeft > Math.min(gsf.getCountdownLength(gameState), COUNT_FROM)) {
    return null;
  }
  const text = timeLeft > 0 ? Math.ceil(timeLeft) : "GO";
  const opacity = (timeLeft - Math.floor(timeLeft)) * 0.8;
  const fontSize = (timeLeft - Math.floor(timeLeft)) * 200;
  return (
    <Overlay gameState={gameState} className="canvas-overlay-text">
      <div style={{ opacity, fontSize }}>{text}</div>
    </Overlay>
  );
}
